import { supabase } from '../utils/supabase/client';

class FactoryServiceAPI {
  constructor() {
    // Detectar se está rodando no Vercel ou localmente
    this.isVercel = typeof window !== 'undefined' && 
      (window.location.protocol === 'https:' || 
       window.location.hostname.includes('vercel.app') || 
       window.location.hostname.includes('vercel.com') ||
       window.location.hostname.includes('gpreto.space') ||
       window.location.hostname !== 'localhost');
    
    // URL base da API
    this.apiUrl = this.isVercel 
      ? '/api'  // Vercel Functions
      : 'http://localhost:3001/api';  // Servidor local
  }

  async createFactory(factoryData) {
    try {
      const FACTORY_COLUMNS = ['name', 'contactName', 'phone', 'wechat', 'email', 'location', 'segment', 'description', 'imageUrl1', 'imageUrl2'];
      const cleanData = Object.fromEntries(
        Object.entries(factoryData).filter(([k, v]) => FACTORY_COLUMNS.includes(k) && v !== undefined)
      );
      const timestamp = new Date().toISOString();
      const { data, error } = await supabase
        .from('factories')
        .insert([{ ...cleanData, createdAt: timestamp, updatedAt: timestamp }])
        .select('id');

      if (error) {
        throw error;
      }

      const newFactory = { id: data?.[0]?.id, ...factoryData };
      await this.syncWithFirebaseAndClearCache();
      window.dispatchEvent(new CustomEvent('factoryCreated', { detail: { factoryId: newFactory.id } }));
      return newFactory;
    } catch (error) {
      throw error;
    }
  }

  async getAllFactories() {
    try {

      const { data, error } = await supabase
        .from('factories')
        .select('*')
        .order('createdAt', { ascending: false })
        .limit(100);

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      throw error;
    }
  }

  async updateFactory(id, factoryData) {
    try {
      const timestamp = new Date().toISOString();
      const FACTORY_COLUMNS = ['name', 'contactName', 'phone', 'wechat', 'email', 'location', 'segment', 'description', 'imageUrl1', 'imageUrl2'];
      const cleanData = Object.fromEntries(
        Object.entries(factoryData).filter(([k, v]) => FACTORY_COLUMNS.includes(k) && v !== undefined)
      );

      const { error } = await supabase
        .from('factories')
        .update({ ...cleanData, updatedAt: timestamp })
        .eq('id', id);

      if (error) {
        throw error;
      }

      const result = { id, ...factoryData };
      await this.syncWithFirebaseAndClearCache();
      window.dispatchEvent(new CustomEvent('factoryUpdated', { detail: { factoryId: id } }));
      return result;
    } catch (error) {
      throw error;
    }
  }

  // Detectar se é mobile
  isMobile() {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  }

  // Limpar cache após mutações
  async syncWithFirebaseAndClearCache() {
    try {
      const cacheService = await import('./cacheService');
      await cacheService.default.clearAll();
    } catch (error) {
      // não propagar erro de cache
    }
  }

  async deleteFactory(id) {
    try {
      const { error } = await supabase
        .from('factories')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      await this.syncWithFirebaseAndClearCache();
      window.dispatchEvent(new CustomEvent('factoryDeleted', { detail: { factoryId: id } }));
      return true;
    } catch (error) {
      throw error;
    }
  }

  async getFactoryById(id) {
    try {
      const { data, error } = await supabase
        .from('factories')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          throw new Error('Documento não encontrado');
        }
        throw error;
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  async getProductsByFactory(factoryId) {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('factoryId', factoryId)
        .order('createdAt', { ascending: false })
        .limit(100);

      if (error) {
        throw error;
      }

      const products = (data || []).map(product => {
        if (product.price && typeof product.price === 'string') {
          return { ...product, price: parseFloat(product.price) };
        }
        return product;
      });

      return products;
    } catch (error) {
      throw error;
    }
  }

  async getFactoryWithProducts(factoryId) {
    try {
      const factory = await this.getFactoryById(factoryId);
      const products = await this.getProductsByFactory(factoryId);
      return { ...factory, products };
    } catch (error) {
      throw error;
    }
  }

  // Método para testar conexão com a API
  async testConnection() {
    try {
      const response = await fetch(`${this.apiUrl}/test`);
      if (response.ok) {
        await response.json();
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }
}

const factoryServiceAPI = new FactoryServiceAPI();
export default factoryServiceAPI;
